import { Button } from '../../../components/ui/button';
import { GiftsBlock, RsvpBlock, renderGalleryByPosition } from '../shared/TemplateBlocks';

const getLocationSummary = (site, locationCards) => {
  if (site.same_location && locationCards?.[0]) {
    return {
      ceremony: locationCards[0],
      reception: {
        title: 'Recepção',
        location: 'Mesmo local',
        address: locationCards[0].address || locationCards[0].location,
        time: site.reception_time || site.ceremony_time
      }
    };
  }

  return {
    ceremony: locationCards?.[0] || {
      title: 'Cerimônia',
      location: site.ceremony_location,
      address: site.ceremony_address,
      time: site.ceremony_time
    },
    reception: locationCards?.[1] || {
      title: 'Recepção',
      location: site.reception_location,
      address: site.reception_address,
      time: site.reception_time
    }
  };
};

export default function SunsetRomanceTemplate(props) {
  const {
    site,
    theme,
    weddingDateLabel,
    daysUntil,
    locationCards,
    availableGifts,
    galleryPosition,
    isTemplatePreview,
    onTemplatePreviewRedirect,
    onOpenRsvp,
    onGiftClick,
    onOpenGallery,
    showCountdown,
    showGiftSection,
    showRsvpCount,
    confirmedGuests
  } = props;

  const primary = theme.primaryColor || '#1A0A04';
  const secondary = theme.secondaryColor || '#E8603A';
  const cream = theme.bannerSectionBackground || '#FFF3E8';
  const page = theme.storySectionBackground || '#FFF9F5';
  const titleColor = theme.heroTitleColor || primary;
  const bodyColor = theme.bannerTextColor || theme.bodyTextColor || primary;
  const { ceremony, reception } = getLocationSummary(site, locationCards);

  const details = [
    {
      label: 'Cerimônia',
      value: ceremony?.location || ceremony?.title || 'Local da cerimônia',
      sub: [ceremony?.time, ceremony?.address].filter(Boolean).join(' · ')
    },
    {
      label: 'Recepção',
      value: reception?.location || reception?.title || 'Local da recepção',
      sub: [reception?.time, reception?.address].filter(Boolean).join(' · ')
    },
    {
      label: showRsvpCount ? 'Confirmados' : 'Contagem',
      value: showRsvpCount ? `${confirmedGuests || 0} pessoa(s)` : `${daysUntil} dias`,
      sub: showRsvpCount ? 'Já confirmaram presença' : 'Até o grande dia'
    }
  ];

  const galleryNode = site.gallery_images?.length ? (
    <section style={{ background: theme.gallerySectionBackground || page }}>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {site.gallery_images.slice(0, 8).map((img, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onOpenGallery?.(idx)}
            className="group h-44 overflow-hidden md:h-72"
            aria-label={`Abrir foto ${idx + 1}`}
          >
            <img
              src={img}
              alt={`Galeria ${idx + 1}`}
              className="h-full w-full object-cover saturate-110 transition duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>
    </section>
  ) : null;

  return (
    <>
      <section
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-20 text-center"
        style={{
          background: theme.bannerSectionBackground
            ? theme.bannerSectionBackground
            : `radial-gradient(circle at 52% 48%, rgba(255,255,255,0.1), transparent 33%), linear-gradient(135deg, ${primary}, ${cream})`
        }}
      >
        <div
          className="pointer-events-none absolute -bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: `${secondary}55` }}
        />
        <div className="relative z-10 max-w-5xl">
          <p
            className="mb-8 text-[0.62rem] uppercase tracking-[0.46em]"
            style={{ color: secondary, fontFamily: theme.bannerTextFont }}
          >
            Virei Noiva · Casamento
          </p>
          <h1
            className="text-5xl italic leading-[0.95] md:text-7xl lg:text-8xl"
            style={{ color: titleColor, fontFamily: theme.bannerTitleFont || theme.headingFont, fontWeight: 400 }}
          >
            {site.couple_names}
          </h1>
          <div className="mx-auto my-8 h-px w-16" style={{ background: secondary }} />
          <p
            className="mx-auto max-w-3xl text-xs uppercase tracking-[0.22em] md:text-sm"
            style={{ color: bodyColor, fontFamily: theme.bannerTextFont }}
          >
            {weddingDateLabel}
          </p>
          {showCountdown ? (
            <p
              className="mt-6 text-xs uppercase tracking-[0.28em]"
              style={{ color: secondary, fontFamily: theme.bannerTextFont }}
            >
              {daysUntil} dias para celebrar
            </p>
          ) : null}
          <Button
            onClick={() => (isTemplatePreview ? onTemplatePreviewRedirect?.() : onOpenRsvp?.())}
            className="mt-10 rounded-none px-10 uppercase tracking-[0.26em]"
            style={{
              background: theme.confirmButtonBackground || primary,
              color: theme.confirmButtonText || cream,
              borderColor: theme.confirmButtonBorder || primary,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            Confirmar Presença
          </Button>
        </div>
      </section>

      {renderGalleryByPosition(galleryPosition, 'after_hero', galleryNode)}

      {site.story_text ? (
        <section className="px-6 py-20" style={{ background: page }}>
          <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-2">
            <div
              className="flex min-h-[420px] items-center justify-center overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${cream}, ${secondary})` }}
            >
              {theme.heroImage ? (
                <img src={theme.heroImage} alt={site.couple_names} className="h-full min-h-[420px] w-full object-cover saturate-110" />
              ) : (
                <span className="text-[0.68rem] uppercase tracking-[0.24em]" style={{ color: primary, opacity: 0.42 }}>
                  Foto do casal
                </span>
              )}
            </div>
            <div>
              <p
                className="mb-6 text-[0.58rem] uppercase tracking-[0.46em]"
                style={{ color: secondary, fontFamily: theme.storyBodyFont }}
              >
                Nossa história
              </p>
              <h2
                className="mb-6 text-3xl italic leading-tight md:text-5xl"
                style={{ color: theme.storyTitleColor || primary, fontFamily: theme.storyTitleFont || theme.headingFont, fontWeight: 400 }}
              >
                Como tudo começou
              </h2>
              <p
                className="whitespace-pre-line text-sm leading-8 md:text-base"
                style={{ color: theme.storyBodyColor || bodyColor, fontFamily: theme.storyBodyFont, opacity: 0.68 }}
              >
                {site.story_text}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {renderGalleryByPosition(galleryPosition, 'after_story', galleryNode)}

      <section className="px-6 py-20" style={{ background: theme.locationSectionBackground || primary }}>
        <h2
          className="mb-12 text-center text-3xl italic md:text-4xl"
          style={{ color: theme.locationTitleColor || cream, fontFamily: theme.locationTitleFont || theme.headingFont, fontWeight: 400 }}
        >
          O grande dia
        </h2>
        <div className="mx-auto grid max-w-5xl gap-px md:grid-cols-3" style={{ background: 'rgba(255,255,255,0.08)' }}>
          {details.map((item) => (
            <div
              key={item.label}
              className="p-8 text-center"
              style={{ background: theme.locationCardBackground || 'rgba(255,255,255,0.04)' }}
            >
              <p
                className="mb-4 text-[0.58rem] uppercase tracking-[0.4em]"
                style={{ color: theme.locationIconColor || secondary, fontFamily: theme.locationTextFont }}
              >
                {item.label}
              </p>
              <p
                className="mb-3 text-xl italic"
                style={{ color: theme.locationTitleColor || cream, fontFamily: theme.locationTitleFont || theme.headingFont, fontWeight: 400 }}
              >
                {item.value}
              </p>
              <p
                className="text-xs leading-6"
                style={{ color: theme.locationTextColor || 'rgba(255,243,232,0.58)', fontFamily: theme.locationTextFont }}
              >
                {item.sub}
              </p>
            </div>
          ))}
        </div>
      </section>

      {renderGalleryByPosition(galleryPosition, 'before_gifts', galleryNode)}

      <GiftsBlock
        gifts={availableGifts}
        headingFont={theme.headingFont}
        theme={theme}
        titleColor={primary}
        textColor={theme.bodyTextColor}
        secondaryColor={secondary}
        onGiftClick={onGiftClick}
        compact
        showGiftSection={showGiftSection}
      />

      {renderGalleryByPosition(galleryPosition, 'end', galleryNode)}

      <RsvpBlock
        onOpenRsvp={onOpenRsvp}
        isTemplatePreview={isTemplatePreview}
        onTemplatePreviewRedirect={onTemplatePreviewRedirect}
        primaryColor={primary}
        secondaryColor={secondary}
        sectionBackground={theme.rsvpSectionBackground || cream}
        buttonBackground={theme.confirmButtonBackground || primary}
        buttonText={theme.confirmButtonText || cream}
        buttonBorder={theme.confirmButtonBorder || primary}
        iconColor={theme.rsvpIconColor || secondary}
        titleColor={theme.rsvpTextColor || primary}
        bodyColor={theme.rsvpTextColor || bodyColor}
        countColor={theme.rsvpTextColor || bodyColor}
        headingFont={theme.rsvpTitleFont || theme.headingFont}
        bodyFont={theme.rsvpTextFont || theme.bannerTextFont}
        showRsvpCount={showRsvpCount}
        confirmedGuests={confirmedGuests}
      />

      <footer className="px-6 py-12 text-center" style={{ background: primary }}>
        <div className="text-3xl italic" style={{ color: cream, fontFamily: theme.headingFont, fontWeight: 400 }}>
          {site.couple_names}
        </div>
        <div className="mt-2 text-[0.65rem] uppercase tracking-[0.35em]" style={{ color: 'rgba(255,243,232,0.38)', fontFamily: theme.bannerTextFont }}>
          Virei Noiva
        </div>
      </footer>
    </>
  );
}

